/**
 * Inline AI model + reasoning-effort selector for chat conversations.
 *
 * Two controls, one line, both compact: the model (a chip, opening a searchable
 * popover of categories) and the effort (a dial, opening the level list this
 * model accepts). Effort sits here because it is a per-question decision —
 * "think harder about this one" — and the effort a model can be asked for
 * depends on which model is chosen, so the two belong together.
 *
 * The effort control is OPT-IN: it renders only when the host passes
 * `onReasoningEffortChange`. Today that is the two surfaces where the
 * conversation is the product — the AI Chat page and the Luma side panel. The
 * embedded task chats (forms wizard, website editor, products dashboard) leave
 * it off deliberately: they are narrow single-purpose flows where a second
 * model-tuning knob is noise. The agent chat panel leaves it off because an
 * agent's effort belongs to its Processing-tab lane, not to one conversation.
 * Passing the handler is all it takes to add it to any of them.
 */
import { useTranslation } from 'react-i18next';
import { ModelCategoryPicker } from '../common/ModelCategoryPicker';
import { ReasoningEffortPicker } from '../common/ReasoningEffortPicker';
import { useAvailableModelCategories } from '../../hooks/useAvailableModelCategories';

interface ChatModelSelectorProps {
  /** Current model category for this session (null = default) */
  sessionModelCategory: string | null;
  /** Display name of the current effective model */
  modelName: string | null;
  /** Whether model selection is allowed */
  allowModelChoice: boolean;
  /** Called when user selects a different model category */
  onModelChange: (categoryId: string | null) => void;
  /** Current reasoning-effort override for this session (null = inherit) */
  sessionReasoningEffort?: string | null;
  /** Called when the user changes the effort (null = back to inherit) */
  onReasoningEffortChange?: (level: string | null) => void;
  /** Whether the selector is disabled (e.g., no active session) */
  disabled?: boolean;
}

export const ChatModelSelector: React.FC<ChatModelSelectorProps> = ({
  sessionModelCategory,
  modelName,
  allowModelChoice,
  onModelChange,
  sessionReasoningEffort = null,
  onReasoningEffortChange,
  disabled = false,
}) => {
  const { t } = useTranslation('common');
  const { data: categories } = useAvailableModelCategories();

  if (!modelName) return null;

  // No model choice allowed — show static caution text
  if (!allowModelChoice) {
    return (
      <p className="text-center text-xs text-muted-foreground opacity-70">
        {t('ai.modelCaution', { model: modelName })}
      </p>
    );
  }

  // Effort options follow the SELECTED category. When the chat is on the
  // organization default there is no category to read them from, so the effort
  // control stays hidden rather than guessing a level list that might not
  // survive the save.
  const selected = categories?.find((c) => c.id === sessionModelCategory) ?? null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
      <ModelCategoryPicker
        value={sessionModelCategory}
        onChange={onModelChange}
        categories={categories ?? []}
        variant="compact"
        disabled={disabled}
        defaultMode="tenant"
        defaultResolvedName={modelName ?? undefined}
        fallbackLabel={modelName ?? undefined}
      />
      {onReasoningEffortChange && selected && (
        <ReasoningEffortPicker
          value={sessionReasoningEffort}
          onChange={onReasoningEffortChange}
          allowedLevels={selected.reasoning_allowed_levels}
          defaultLevel={selected.reasoning_level}
          variant="compact"
          disabled={disabled}
        />
      )}
    </div>
  );
};
